// ── Inventory insights ──────────────────────────────────────────────────────
//
// Reads the getInventoryTrend payload and says what changed. One rule set, in
// one file, called by both the certificate page and the collateral dashboard —
// because two copies of "what counts as a surge" is two answers to the same
// question, and the second one is always the stale one.
//
// WHAT EARNS A LINE HERE. Only things a reader would act on or ask about:
// a category that moved, a category that stopped counting, growth the
// borrowing base did not get. Everything else is already on the charts, and a
// panel that restates the chart teaches people to skip the panel.
//
// EVERY FINDING CARRIES ITS OWN NUMBERS. "Finished goods up" is a claim;
// "finished goods up $800K (36%) since 06/30" is something you can check
// against the table underneath. A finding that cannot be checked is a finding
// nobody trusts twice.
//
// NOTHING HERE IS A RECOMMENDATION. These say what the certificates show, not
// what to do about it — the reader knows the credit and this file does not.

// Thresholds, named and in one place so they can be argued with. They are
// deliberately not tight: this panel competes for attention with the charts,
// and a panel that fires on every wobble is one people stop reading.
export const TH = {
  surgePct:      0.20,   // period-over-period move that counts as a surge
  surgeFloor:    50000,  // ...and the dollars below which a big % is just noise
  runLength:     3,      // consecutive periods in one direction to call a trend
  runPct:        0.15,   // ...and the net move across that run
  mixShiftPts:   8,      // percentage POINTS of mix shift worth naming
  ineligibleCut: 0.50,   // share of a category's gross that is ineligible
  leakPct:       0.10,   // gross growth that did not reach net available
};

const num = (v) => (v == null || v === "" ? 0 : (parseFloat(v) || 0));
const pct = (a, b) => (b ? (a - b) / Math.abs(b) : null);
const money = (v) => {
  const a = Math.abs(v);
  const s = a >= 1e6 ? "$" + (v / 1e6).toFixed(1) + "M"
          : a >= 1e3 ? "$" + Math.round(v / 1e3) + "K"
          : "$" + Math.round(v);
  return s.replace("$-", "-$");
};
const pctStr = (p) => (p == null ? "—" : (p >= 0 ? "+" : "") + Math.round(p * 100) + "%");
const dateStr = (d) => String(d || "").slice(0, 10);

// The six buckets bbcParser normalises into, in reading order.
const LABELS = {
  RM: "Raw material", WIP: "WIP", FG: "Finished goods",
  "Packaging/Supplies": "Packaging", General: "General", Other: "Other",
};
const nameOf = (k) => LABELS[k] || k || "Uncategorised";
// Mid-sentence form. Lower-casing blindly turned "WIP" into "wip", which reads
// as a typo — an acronym is not a word that happens to be capitalised.
const midName = (k) => {
  const n = nameOf(k);
  return /^[A-Z]{2,}$/.test(n) ? n : n.charAt(0).toLowerCase() + n.slice(1);
};

/**
 * @param {{header: Array, categories: Array}} trend  getInventoryTrend payload
 * @returns {Array<{key,severity,title,detail}>}  severity: "watch" | "info"
 */
export const inventoryInsights = (trend) => {
  const header = (trend && trend.header) || [];
  const cats   = (trend && trend.categories) || [];
  if (!header.length) return [];

  const out = [];
  const periods = header.map(h => ({ bbid: h.bbid, asof: dateStr(h.asofdate) }));
  const last = header[header.length - 1];
  const prev = header.length > 1 ? header[header.length - 2] : null;
  // Two kinds of rule, and only one of them needs history. "WIP is entirely
  // ineligible" and "a sublimit is capping this" are true of a single
  // certificate — gating them behind a second period would mean a borrower's
  // first upload said nothing at all, which is exactly when a reader is
  // looking hardest.
  const hasHistory = header.length > 1;

  const at = (bucket, bbid, measure) => {
    const r = cats.find(c => c.category_norm === bucket && c.bbid === bbid);
    return r ? num(r[measure]) : 0;
  };
  // Only buckets this borrower actually reports. A finding about a bucket that
  // is zero in every period is noise dressed as analysis.
  const buckets = [...new Set(cats.map(c => c.category_norm))]
    .filter(b => periods.some(p => at(b, p.bbid, "gross") !== 0));

  // ── 1. a category that moved, period over period ──────────────────────────
  for (const b of hasHistory ? buckets : []) {
    const now = at(b, last.bbid, "gross"), was = at(b, prev.bbid, "gross");
    const d = now - was, p = pct(now, was);
    if (p == null || Math.abs(d) < TH.surgeFloor || Math.abs(p) < TH.surgePct) continue;
    out.push({
      key: "move:" + b,
      severity: "watch",
      title: `${nameOf(b)} ${d > 0 ? "up" : "down"} ${pctStr(p)} this period`,
      detail: `${money(was)} at ${dateStr(prev.asofdate)}`
            + ` to ${money(now)} at ${dateStr(last.asofdate)}, a change of ${money(d)}.`,
    });
  }

  // ── 2. a direction held for several periods ───────────────────────────────
  // Not the same finding as 1: a category can move hard once and mean nothing,
  // or drift 6% a period for a year and be the thing that matters.
  for (const b of hasHistory ? buckets : []) {
    const series = periods.map(p => at(b, p.bbid, "gross"));
    if (series.length < TH.runLength) continue;
    let run = 1, dir = 0;
    for (let i = series.length - 1; i > 0; i--) {
      const step = Math.sign(series[i] - series[i - 1]);
      if (step === 0) break;
      if (dir === 0) dir = step;
      else if (step !== dir) break;
      if (i < series.length - 1) run++;
    }
    if (run < TH.runLength || dir === 0) continue;
    const from = series[series.length - run - 1], to = series[series.length - 1];
    const p = pct(to, from);
    if (p == null || Math.abs(p) < TH.runPct) continue;
    out.push({
      key: "run:" + b,
      severity: "watch",
      title: `${nameOf(b)} has ${dir > 0 ? "risen" : "fallen"} ${run} periods running`,
      detail: `${money(from)} to ${money(to)} since ${periods[periods.length - run - 1].asof}`
            + ` — ${pctStr(p)} over the run.`,
    });
  }

  // ── 3. the mix itself shifted ─────────────────────────────────────────────
  // Every category can grow and the mix not move at all. This is the question
  // the totals cannot answer: is the inventory becoming a different thing?
  {
    const first = hasHistory ? header[0] : null;
    const share = (bbid) => {
      const tot = buckets.reduce((a, b) => a + at(b, bbid, "gross"), 0);
      return tot ? Object.fromEntries(buckets.map(b => [b, at(b, bbid, "gross") / tot])) : null;
    };
    const s0 = first ? share(first.bbid) : null, s1 = share(last.bbid);
    if (s0 && s1) {
      for (const b of buckets) {
        const pts = (s1[b] - s0[b]) * 100;
        if (Math.abs(pts) < TH.mixShiftPts) continue;
        out.push({
          key: "mix:" + b,
          severity: "info",
          title: `${nameOf(b)} is ${pts > 0 ? "a bigger" : "a smaller"} share of inventory than it was`,
          detail: `${Math.round(s0[b] * 100)}% of gross at ${dateStr(first.asofdate)}`
                + ` to ${Math.round(s1[b] * 100)}% at ${dateStr(last.asofdate)}`
                + ` (${pts > 0 ? "+" : ""}${Math.round(pts)} points).`,
        });
      }
    }
  }

  // ── 4. a category carrying most of the ineligibles ────────────────────────
  for (const b of buckets) {
    const g = at(b, last.bbid, "gross"), i = at(b, last.bbid, "ineligible");
    if (!g || i / g < TH.ineligibleCut) continue;
    const allIneligible = i >= g - 1;
    out.push({
      key: "inelig:" + b,
      severity: "watch",
      title: allIneligible
        ? `${nameOf(b)} is entirely ineligible`
        : `${Math.round((i / g) * 100)}% of ${midName(b)} is ineligible`,
      detail: `${money(g)} on hand, ${money(i)} excluded`
            + `${allIneligible ? " — it is on the certificate but contributing nothing." : "."}`,
    });
  }

  // ── 5. growth the borrowing base did not get ──────────────────────────────
  // The finding people actually want and no chart states outright: inventory
  // grew, availability did not, so the growth went into ineligibles, the
  // advance rate, or a cap.
  {
    const g0 = num(prev && prev.inv_gross), g1 = num(last.inv_gross);
    const n0 = num(prev && prev.inv_net_available), n1 = num(last.inv_net_available);
    const gp = hasHistory ? pct(g1, g0) : null, np = hasHistory ? pct(n1, n0) : null;
    if (gp != null && np != null && gp >= TH.leakPct && np < gp - TH.leakPct) {
      out.push({
        key: "leak",
        severity: "watch",
        title: "Inventory grew faster than what it makes available",
        detail: `Gross ${pctStr(gp)} (${money(g0)} to ${money(g1)}) but net available ${pctStr(np)}`
              + ` (${money(n0)} to ${money(n1)}). The difference is in ineligibles, the advance`
              + ` rate, or a cap.`,
      });
    }
  }

  // ── 6. a cap, not a calculation, is setting the number ────────────────────
  {
    const avail = num(last.inv_available), net = num(last.inv_net_available);
    const held = avail - net;
    if (avail > 0 && held > Math.max(TH.surgeFloor, avail * 0.02)) {
      const parts = [
        num(last.inv_sublimit) ? `sublimit ${money(num(last.inv_sublimit))}` : null,
        num(last.inv_reserve) ? `reserve ${money(num(last.inv_reserve))}` : null,
      ].filter(Boolean);
      out.push({
        key: "cap",
        severity: "info",
        title: "A sublimit or reserve is holding back inventory availability",
        detail: `${money(avail)} after the advance rate, ${money(net)} after`
              + `${parts.length ? " " + parts.join(" and ") : " caps"}`
              + ` — ${money(held)} held back.`,
      });
    }
  }

  // Watch items first, then in the order the rules produced them: a panel that
  // sorts by size would bury a category that went to zero under one that moved
  // a larger number for an ordinary reason.
  return out.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "watch" ? -1 : 1));
};
